/* ──────────────────────────────────────────────────────────────────────
 * ResetPasswordPage — public landing page for SMS / email reset links.
 *
 * Mounted at `/reset-password` BEFORE the LoginProvider auth gate so a
 * user without an active session can complete the flow. Reads the
 * `?token=` query parameter, pings /api/auth/reset-password/validate to
 * check liveness, and renders one of three states:
 *
 *   1. validating  — spinner while the token check is in flight
 *   2. valid       — the new-password form
 *   3. invalid     — friendly "expired or invalid" panel with a CTA back
 *                    to /forgot-password
 *
 * On successful submission redirects to /login?reset=success so the
 * Login page can show its confirmation banner.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, CircularProgress, IconButton,
  InputAdornment, LinearProgress, Stack, TextField, Typography,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import LoginImg from "../../Images/background.png";
import Logo from "../../Images/logo_main_black.webp";
import {
  validateResetToken, submitPasswordReset, type ResetTokenValidation,
} from "../../Api/rbacApi";

const NAVY = "#0d1b2a";
const GOLD = "#c9a84c";

/* Tiny client-side strength meter — purely indicative; the backend's
 * `min:8` validator is the authoritative gate. */
function scorePassword(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string; color: string } {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  const map = [
    { label: "—",      color: "#cbd5e1" },
    { label: "Weak",   color: "#dc2626" },
    { label: "Fair",   color: "#f59e0b" },
    { label: "Good",   color: "#16a34a" },
    { label: "Strong", color: "#15803d" },
  ];
  return { score: s as 0 | 1 | 2 | 3 | 4, ...map[s] };
}

const ResetPasswordPage: React.FC = () => {
  // Responsive layout via sx-prop arrays; no useMediaQuery needed.
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get("token") ?? "";

  const [validation, setValidation] = useState<ResetTokenValidation | "loading">("loading");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── Token validation on mount ─────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setValidation({ valid: false, message: "Reset link is missing its token. Request a new reset link." });
      return;
    }
    validateResetToken(token)
      .then((res) => { if (!cancelled) setValidation(res); })
      .catch(() => {
        if (!cancelled) setValidation({
          valid: false,
          message: "Reset link could not be verified. Request a new reset link.",
        });
      });
    return () => { cancelled = true; };
  }, [token]);

  const strength = useMemo(() => scorePassword(password), [password]);
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = passwordsMatch && password.length >= 8 && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await submitPasswordReset(token, password, confirm);
      // Redirect to login with a flag so the Login page can show a
      // success banner. We use ?reset=success rather than a toast so it
      // survives the page reload that clears Redux state.
      navigate("/login?reset=success", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Password reset failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <Box sx={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "center", md: "flex-end" },
        px: { xs: 2, sm: 3, md: 0 },
        pr: { md: 20 },
      }}>
        <Box component="img" src={LoginImg} alt="Background"
          sx={{ objectFit: "cover", position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0 }} />
        <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", zIndex: 0 }} />

        <Box sx={{
          position: "relative", zIndex: 1,
          width: "100%",
          maxWidth: { xs: 420, sm: 440, md: 460 },
        }}>
            <Card sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 2,
              boxShadow: "0 18px 48px rgba(0,0,0,0.35)",
              bgcolor: "#fff",
              boxSizing: "border-box",
              width: "100%",
            }}>
              <Box sx={{ display: "flex", justifyContent: "center", pb: 3 }}>
                <img src={Logo} alt="Durham Convention Centre" style={{ maxWidth: 100, height: "auto" }} />
              </Box>

              {validation === "loading" && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={28} sx={{ color: NAVY }} />
                  <Typography sx={{ mt: 2, color: "#475569", fontSize: 13 }}>
                    Verifying reset link…
                  </Typography>
                </Box>
              )}

              {validation !== "loading" && !validation.valid && (
                <Box sx={{ textAlign: "center" }}>
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 44, color: "#dc2626", mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>
                    Reset link expired or invalid
                  </Typography>
                  <Typography sx={{ color: "#475569", fontSize: 13.5, mt: 1, mb: 3 }}>
                    {validation.message ?? "Request a new reset link to continue."}
                  </Typography>
                  <Stack gap={1.2}>
                    <Button
                      component={RouterLink}
                      to="/forgot-password"
                      variant="contained"
                      sx={{ bgcolor: NAVY, textTransform: "none", fontWeight: 700,
                        "&:hover": { bgcolor: "#1e3a5f" } }}
                    >
                      Request a new reset link
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/login"
                      sx={{ textTransform: "none", color: GOLD, fontWeight: 600 }}
                    >
                      Back to login
                    </Button>
                  </Stack>
                </Box>
              )}

              {validation !== "loading" && validation.valid && (
                <form onSubmit={handleSubmit}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY, textAlign: "center" }}>
                    Set a new password
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475569", textAlign: "center", mt: 0.5, mb: 2.5 }}>
                    {validation.user?.first_name || validation.user?.display_name
                      ? `Hi ${validation.user.first_name ?? validation.user.display_name}, choose a strong password to secure your account.`
                      : "Choose a strong password to secure your account."}
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: 13 } }}>
                      {error}
                    </Alert>
                  )}

                  <TextField
                    fullWidth autoFocus
                    label="New password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPw((p) => !p)} edge="end">
                            {showPw ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText="Minimum 8 characters."
                    sx={{ mb: 1 }}
                  />

                  {password.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(strength.score / 4) * 100}
                        sx={{
                          height: 6, borderRadius: 1, bgcolor: "#f1f5f9",
                          "& .MuiLinearProgress-bar": { bgcolor: strength.color },
                        }}
                      />
                      <Typography sx={{ fontSize: 11, mt: 0.5, color: strength.color, fontWeight: 700 }}>
                        Strength: {strength.label}
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    label="Confirm new password"
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    error={confirm.length > 0 && !passwordsMatch}
                    helperText={
                      confirm.length > 0 && !passwordsMatch
                        ? "Passwords don't match."
                        : " "
                    }
                    sx={{ mb: 1.5 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!canSubmit}
                    startIcon={submitting
                      ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                      : <LockResetRoundedIcon />}
                    sx={{
                      bgcolor: NAVY, color: "#fff", py: 1.2,
                      textTransform: "none", fontWeight: 700,
                      "&:hover": { bgcolor: "#1e3a5f" },
                      "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" },
                    }}
                  >
                    {submitting ? "Updating password…" : "Reset password"}
                  </Button>
                </form>
              )}
            </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
